import React from 'react';
import { IonButton, IonIcon, IonBadge } from '@ionic/react';
import { helpCircleOutline, rocketOutline, checkmarkCircleOutline } from 'ionicons/icons';
import { useOnboarding } from '../hooks/useOnboarding';

interface OnboardingButtonProps {
  variant?: 'icon' | 'text' | 'full';
  size?: 'small' | 'default' | 'large';
  fill?: 'clear' | 'outline' | 'solid';
  className?: string;
}

const OnboardingButton: React.FC<OnboardingButtonProps> = ({
  variant = 'full',
  size = 'default',
  fill = 'clear',
  className = '',
}) => {
  const { showOnboarding, progress, getCompletionPercentage } = useOnboarding();

  const handleClick = () => {
    showOnboarding();
  };

  if (!progress) return null;

  const completionPercentage = getCompletionPercentage();
  const isComplete = progress.isCompleted;

  if (variant === 'icon') {
    return (
      <IonButton
        fill={fill}
        size={size}
        onClick={handleClick}
        className={`${className} relative`}
        title={`Show Getting Started Guide ${isComplete ? '(Complete!)' : `(${completionPercentage}%)`}`}
      >
        <IonIcon icon={isComplete ? checkmarkCircleOutline : helpCircleOutline} />
        {!isComplete && completionPercentage > 0 && (
          <IonBadge 
            color="primary" 
            className="absolute -top-1 -right-1 text-xs min-w-6 h-5"
          >
            {completionPercentage}%
          </IonBadge>
        )}
      </IonButton>
    );
  }

  if (variant === 'text') {
    return (
      <IonButton
        fill={fill}
        size={size}
        onClick={handleClick}
        className={`${className} relative`}
      >
        {isComplete ? 'Setup Complete!' : 'Getting Started'}
        {!isComplete && completionPercentage > 0 && (
          <IonBadge 
            color="primary" 
            className="ml-2 text-xs"
          >
            {completionPercentage}%
          </IonBadge>
        )}
      </IonButton>
    );
  }

  return (
    <IonButton
      fill={fill}
      size={size}
      onClick={handleClick}
      className={`${className} flex items-center`}
    >
      <IonIcon icon={isComplete ? checkmarkCircleOutline : rocketOutline} slot="start" />
      <span className="flex items-center gap-2">
        {isComplete ? 'Setup Complete!' : 'Getting Started Guide'}
        {!isComplete && completionPercentage > 0 && (
          <IonBadge 
            color="primary" 
            className="text-xs"
          >
            {completionPercentage}%
          </IonBadge>
        )}
      </span>
    </IonButton>
  );
};

export default OnboardingButton;